import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentsAPI, settingsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Loader2, Star, TrendingUp, ClipboardCheck, Building2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { formatDate } from '../lib/dateUtils';

const PerformanceReport = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [logo, setLogo] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportRes, settingsRes] = await Promise.all([
          studentsAPI.getPerformanceReport(studentId),
          settingsAPI.get().catch(() => null),
        ]);
        setReport(reportRes.data);
        if (settingsRes?.data?.logo) setLogo(settingsRes.data.logo);
      } catch {
        toast.error('Failed to load performance report');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studentId]);

  const handleExportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const studentName = report.student.name.replace(/\s+/g, '_');
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Performance_Report_${studentName}_${formatDate(new Date().toISOString())}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('Report exported');
    } catch {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const renderStars = (rating, max = 5) => {
    return Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ color: i < rating ? '#d4a843' : '#e5e7eb', fontSize: '14px' }}>★</span>
    ));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ica-navy" />
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Failed to load report data.</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </Layout>
    );
  }

  const { student, overall_average_rating, total_assessments, placements } = report;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(`/students/${studentId}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Profile
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-ica-navy">Performance Report</h1>
              <p className="text-gray-500">{student.name}</p>
            </div>
          </div>
          <Button
            data-testid="export-performance-pdf"
            onClick={handleExportPDF}
            disabled={exporting}
            
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export PDF
          </Button>
        </div>

        {/* Printable Report */}
        <div ref={printRef} style={{ background: '#ffffff', padding: '24px' }}>
          {/* Report Header */}
          <div style={{ borderBottom: '3px solid #1e3a5f', paddingBottom: '16px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {logo && <img src={logo} alt="ICA Logo" style={{ height: '50px', marginBottom: '8px' }} />}
              <h2 style={{ color: '#1e3a5f', fontSize: '22px', fontWeight: 'bold', margin: 0 }}>Student Performance Report</h2>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: '4px 0 0' }}>Generated {formatDate(new Date().toISOString())}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#1e3a5f', fontWeight: '600', fontSize: '16px', margin: 0 }}>{student.name}</p>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: '2px 0' }}>{student.student_number}</p>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: '2px 0' }}>{student.year_of_study} | {student.course_length || '3 Year'} Course{student.course_type ? ` · ${student.course_type}` : ''}</p>
            </div>
          </div>

          {/* Summary Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <TrendingUp className="w-5 h-5 text-ica-gold" />
                    <span className="text-2xl font-bold text-ica-navy">
                      {overall_average_rating !== null ? overall_average_rating : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Overall Average</p>
                  {overall_average_rating && <div>{renderStars(Math.round(overall_average_rating))}</div>}
                </div>
                <div>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                    <span className="text-2xl font-bold text-ica-navy">{total_assessments}</span>
                  </div>
                  <p className="text-sm text-gray-500">Rated Assessments</p>
                </div>
                <div>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                    <span className="text-2xl font-bold text-ica-navy">{placements.length}</span>
                  </div>
                  <p className="text-sm text-gray-500">Placements</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placement Breakdowns */}
          {placements.map((p, pIdx) => (
            <div key={p.placement_id} className="mb-6" style={{ pageBreakInside: 'avoid' }}>
              <div style={{ background: '#f1f5f9', padding: '12px 16px', borderRadius: '8px 8px 0 0', borderBottom: '2px solid #d4a843' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-ica-navy" />
                    <span className="font-semibold text-ica-navy">{p.restaurant_name}</span>
                    <span className="text-sm text-gray-500">({p.position})</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-3.5 h-3.5" />
                    {p.start_date} to {p.end_date}
                  </div>
                </div>
              </div>

              {p.assessments.length === 0 ? (
                <div style={{ padding: '16px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                  <p className="text-sm text-gray-400">No assessments completed.</p>
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: '#fafafa' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#1e3a5f', fontSize: '12px', textTransform: 'uppercase' }}>#</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#1e3a5f', fontSize: '12px', textTransform: 'uppercase' }}>Status</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#1e3a5f', fontSize: '12px', textTransform: 'uppercase' }}>Average</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#1e3a5f', fontSize: '12px', textTransform: 'uppercase' }}>Ratings Breakdown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.assessments.map((a) => (
                        <tr key={a.assessment_number} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 16px', fontWeight: '600' }}>#{a.assessment_number}</td>
                          <td style={{ padding: '10px 16px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              background: a.status === 'submitted' ? '#d1fae5' : '#fef3c7',
                              color: a.status === 'submitted' ? '#065f46' : '#92400e',
                            }}>
                              {a.status === 'submitted' ? 'Completed' : 'Pending'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            {a.average_rating !== null ? (
                              <div className="flex items-center gap-1">
                                <span className="font-semibold text-ica-navy">{a.average_rating}</span>
                                <span className="text-ica-gold">★</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            {Object.keys(a.ratings).length > 0 ? (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {Object.entries(a.ratings).map(([label, val]) => (
                                  <span key={label} className="text-xs text-gray-600">
                                    {label}: <strong>{val}</strong>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">No ratings</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Comments section */}
                  {p.assessments.some(a => Object.keys(a.comments).length > 0) && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a5f', textTransform: 'uppercase', marginBottom: '8px' }}>Chef Comments</p>
                      {p.assessments.filter(a => Object.keys(a.comments).length > 0).map((a) => (
                        <div key={a.assessment_number} className="mb-2">
                          <p className="text-xs font-medium text-gray-600">Assessment #{a.assessment_number}:</p>
                          {Object.entries(a.comments).map(([label, val]) => (
                            <p key={label} className="text-xs text-gray-500 ml-3">
                              <em>{label}:</em> {val}
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {placements.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-400">No placement data available for this student.</p>
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '2px solid #1e3a5f', paddingTop: '12px', marginTop: '24px', textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: '11px' }}>Mizanplaz - The ICA Placement App | Confidential Student Performance Report</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PerformanceReport;
